import { SourceDefinition } from '../models/source-definition.model'
import { API_SOURCE, GridRole, SourceKey } from '../models/source-key.model'

/**
 * The single source of truth for what each source is and does. Everything downstream (source
 * picker, ColDef factory, match-count columns, footer) reads from here, so adding/rewiring a
 * source is a data change, not a code change.
 *
 * The column catalog is no longer part of a source's static definition — it is fetched per grid
 * from the Flex Transactions field-catalog API (see FieldCatalogService) and passed into
 * buildColDefs()/colSourceMap() at rebuild time.
 */
export const SOURCE_REGISTRY: Record<SourceKey, SourceDefinition> = {
  api: {
    key: 'api',
    label: API_SOURCE.name,
    tag: 'API',
    tagColor: '#7d5fff',
    color: '#7d5fff',
    model: 'Event model',
    sub: `Connected via ${API_SOURCE.transport}`,
    allowedRoles: ['base', 'comparison'],
    // Enfusion is the Base feed for now; comparing it against itself in the top grid is not a
    // pairing we are ready to show, so the option is listed but greyed out.
    disabledRoles: ['comparison'],
    matchTargets: ['cust', 'trad', 'cw'],
    hasComments: false,
    hasCreate: true,
    // Enfusion Create names the document first — a Trade submenu of all fourteen trade types, plus
    // GL Entry — per the prototype's `createLoadoutItems`. This is what makes each tran category's
    // create-side metadata (its key groups, and `TRANSACTION_CREATE_DEFAULTS` where it has seeds)
    // reachable rather than provisioned-but-dead; the items themselves live on the grid component.
    hasCreateMenu: true,
  },
  prom: {
    key: 'prom',
    label: 'Prometheus',
    tag: 'EXT',
    tagColor: '#217dfe',
    color: '#1162e7',
    model: 'Normalized',
    sub: 'Custody + Trading',
    allowedRoles: ['comparison'],
    matchTargets: ['api', 'cw'],
    hasAggregateInfo: true,
    hasStatusFilter: true,
    hasComments: true,
  },
  cw: {
    key: 'cw',
    label: 'Clearwater',
    tag: 'CORE',
    tagColor: '#0047d1',
    color: '#0047d1',
    model: 'ABOR',
    sub: 'Golden copy',
    allowedRoles: ['base'],
    // Clearwater has no read endpoint in the workbench yet, so the Base grid stays on Enfusion.
    disabledRoles: ['base'],
    matchTargets: ['cust', 'trad', 'api'],
    hasComments: true,
    hasCreate: true,
    hasCreateMenu: true,
  },
}

export const sourceDef = (key: SourceKey): SourceDefinition =>
  SOURCE_REGISTRY[key]

/**
 * Whether a grid in `role` may actually be pointed at `key` — allowed for the role, and not one of
 * the pairings held back for now.
 *
 * The single answer both the picker and the store use, so a disabled option cannot be reached by
 * clicking it, by a stale persisted source, or by the Enfusion mutual-exclusion swap.
 */
export function isSourceSelectable(role: GridRole, key: SourceKey): boolean {
  const definition = SOURCE_REGISTRY[key]
  return (
    definition.allowedRoles.includes(role) &&
    !(definition.disabledRoles ?? []).includes(role)
  )
}
